/**
 * Farben der Arten (Termine, Beziehungen, Produkt-Tags). Eine Farbe ist der Name einer Vorgabe („teal“, so stehen ältere Einträge in der Datenbank) oder eine beliebige
 * Farbe als `#rrggbb`. Für Vorgaben liefert color.css die Klassen `tone--teal` usw. Für freie Farben legt `toneClass` beim ersten Gebrauch eine Klasse an, die
 * `--tone` (Fläche mit weißer Schrift) und `--tone-dot` (Punkt, im dunklen Design heller) setzt. Die Werte werden so angepasst, dass weiße Schrift auf der Fläche und der
 * Punkt auf dem Hintergrund lesbar bleiben (Kontrast nach WCAG), auch bei sehr hellen oder sehr dunklen Farben.
 */

import './color.css';

export const PRESET_HEX: Record<string, string> = {
  teal: '#0f766e',
  blue: '#1d4ed8',
  violet: '#6d28d9',
  amber: '#b45309',
  red: '#b91c1c',
  green: '#15803d',
  slate: '#475569',
  pink: '#be185d',
};

const HEX = /^#[0-9a-f]{6}$/i;
export const isHex = (v: string): boolean => HEX.test(v);

/** Farbwert als `#rrggbb`: Vorgaben werden in ihren Wert übersetzt, Unbekanntes ergibt die Vorgabe Türkis */
export const toHex = (color: string | undefined): string => {
  if (!color) return PRESET_HEX.teal!;
  const c = color.toLowerCase();
  return isHex(c) ? c : (PRESET_HEX[c] ?? PRESET_HEX.teal!);
};

type Rgb = [number, number, number];
export const hexToRgb = (hex: string): Rgb => [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
export const rgbToHex = ([r, g, b]: Rgb): string => `#${[r, g, b].map((n) => Math.round(Math.max(0, Math.min(255, n))).toString(16).padStart(2, '0')).join('')}`;

const luminance = ([r, g, b]: Rgb): number => {
  const f = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};
export const contrast = (a: Rgb, b: Rgb): number => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
};
const mix = (a: Rgb, b: Rgb, t: number): Rgb => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];
/** Hintergründe, auf denen der Punkt stehen muss: Karte im hellen und im dunklen Design (styles.css: --surface) */
const SURFACE_LIGHT: Rgb = [255, 255, 255];
const SURFACE_DARK: Rgb = [17, 26, 46];

/** Mischt die Farbe mit `towards`, bis sie gegen `against` den Kontrast `min` erreicht */
function reach(color: Rgb, towards: Rgb, against: Rgb, min: number): Rgb {
  let out = color;
  for (let t = 0; t <= 1.0001 && contrast(out, against) < min; t += 0.04) out = mix(color, towards, t);
  return out;
}

/** Anpassung einer freien Farbe: Fläche für weiße Schrift (mindestens 4,5:1), Punkt im hellen (3:1 gegen Weiß) und im dunklen Design (3:1 gegen die dunkle Karte) */
export function readableTone(hex: string): { tone: string; dotLight: string; dotDark: string } {
  const rgb = hexToRgb(hex);
  return {
    tone: rgbToHex(reach(rgb, BLACK, WHITE, 4.5)),
    dotLight: rgbToHex(reach(rgb, BLACK, SURFACE_LIGHT, 3)),
    dotDark: rgbToHex(reach(rgb, WHITE, SURFACE_DARK, 3)),
  };
}

let sheet: HTMLStyleElement | null = null;
const known = new Set<string>();
function define(hex: string): string {
  const name = `tone--c-${hex.slice(1)}`;
  if (known.has(name) || typeof document === 'undefined') return name;
  known.add(name);
  if (!sheet) {
    sheet = document.createElement('style');
    sheet.id = 'tone-custom';
    document.head.appendChild(sheet);
  }
  const t = readableTone(hex);
  sheet.appendChild(document.createTextNode(`.${name}{--tone:${t.tone};--tone-dot:${t.dotLight}}:root[data-theme="dark"] .${name}{--tone-dot:${t.dotDark}}\n`));
  return name;
}

/** CSS-Klasse mit der Farbe einer Art. Ohne Farbe gilt die Vorgabe (Türkis). */
export const toneClass = (color: string | undefined): string => {
  if (!color) return '';
  const c = color.toLowerCase();
  return isHex(c) ? define(c) : `tone--${c}`;
};

// --- Farbmodell für die Palette (HSV) -------------------------------------------------------

export interface Hsv {
  h: number; // 0 bis 360
  s: number; // 0 bis 1
  v: number; // 0 bis 1
}
export function hexToHsv(hex: string): Hsv {
  const [r, g, b] = hexToRgb(hex).map((x) => x / 255) as Rgb;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}
export function hsvToHex({ h, s, v }: Hsv): string {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return rgbToHex([(r + m) * 255, (g + m) * 255, (b + m) * 255]);
}
